import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { signInErrorMessage } from '@/core/auth';
import { Button, cn } from '@/core/ui';
import { ArrowRight } from '@/core/ui/icons';
import { useSignIn } from '../application/session';
import { loginSchema, type LoginForm as LoginValues } from '../domain/login';
import { FieldError, FormAlert, InputShell, inputClass, RevealPasswordButton } from './AuthFields';
import { ForgotPassword } from './ForgotPassword';

/**
 * Formulário de login. Ao dar certo, o token
 * entra no AuthProvider e a rota /login redireciona sozinha.
 *
 * `returnTo`: para onde voltar quando o login sai da SPA ("Esqueceu a
 * senha?" passa pela tela do Keycloak e volta por /auth/callback).
 */
export function LoginForm({
  returnTo,
  defaultUsername = '',
  defaultForgotOpen = false,
}: {
  returnTo?: string;
  /** Vindo do "Criar conta" com um e-mail que já tem conta: já preenchido. */
  defaultUsername?: string;
  /** …e, se a pessoa escolheu "Esqueci a senha", com a redefinição aberta. */
  defaultForgotOpen?: boolean;
}) {
  const signIn = useSignIn();
  const [showPassword, setShowPassword] = useState(false);
  const [forgot, setForgot] = useState(defaultForgotOpen);
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: defaultUsername, password: '' },
  });
  const { errors } = form.formState;
  const userId = useId();
  const passId = useId();
  const userErrorId = useId();
  const passErrorId = useId();

  const submit = form.handleSubmit((values) =>
    signIn.mutateAsync(values).catch(() => {
      // A mensagem vem de signIn.error; aqui só limpa a senha.
      form.resetField('password');
      form.setFocus('password');
    }),
  );

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      {signIn.isError && <FormAlert>{signInErrorMessage(signIn.error)}</FormAlert>}

      <div className="space-y-1.5">
        <label htmlFor={userId} className="block text-sm font-medium text-fg-muted">
          Usuário ou e-mail
        </label>
        <InputShell invalid={!!errors.username}>
          <input
            id={userId}
            autoFocus={!defaultUsername}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="voce@email.com"
            aria-invalid={!!errors.username || undefined}
            aria-describedby={errors.username ? userErrorId : undefined}
            className={inputClass}
            {...form.register('username')}
          />
        </InputShell>
        {errors.username && <FieldError id={userErrorId}>{errors.username.message}</FieldError>}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label htmlFor={passId} className="block text-sm font-medium text-fg-muted">
            Senha
          </label>
          <button
            type="button"
            onClick={() => setForgot((v) => !v)}
            aria-expanded={forgot}
            className="text-sm text-brand-300 transition-colors hover:text-brand-400"
          >
            Esqueceu a senha?
          </button>
        </div>
        <InputShell invalid={!!errors.password}>
          <input
            id={passId}
            type={showPassword ? 'text' : 'password'}
            autoFocus={!!defaultUsername && !defaultForgotOpen}
            autoComplete="current-password"
            placeholder="••••••••"
            aria-invalid={!!errors.password || undefined}
            aria-describedby={errors.password ? passErrorId : undefined}
            className={cn(inputClass, 'pr-11')}
            {...form.register('password')}
          />
          <RevealPasswordButton shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />
        </InputShell>
        {errors.password && <FieldError id={passErrorId}>{errors.password.message}</FieldError>}
        {forgot && <ForgotPassword getUsername={() => form.getValues('username')} returnTo={returnTo} />}
      </div>

      <Button
        type="submit"
        size="lg"
        className="group mt-4 h-[3.25rem] w-full rounded-2xl text-[0.95rem] font-semibold"
        loading={signIn.isPending}
      >
        Entrar
        {!signIn.isPending && (
          <ArrowRight aria-hidden className="size-4 transition-transform group-hover:translate-x-0.5" />
        )}
      </Button>
    </form>
  );
}
